'use client';

import {useMessages} from '@epic-mw/i18n';
import {JSX, useState} from 'react';
import {Text} from '@/components';
import {css} from '@panda/css';
import {AlertFieldsFragment, EdsAlertVariant, EdsStatus} from '@common/hygraph-schema';
import {AlertBanner, AlertBannerProps, AlertInline, AlertInlineProps, Stack} from '@eds-web/react';

type BannerProps = Omit<AlertBannerProps, 'message'> & {alertVariant: EdsAlertVariant.Banner};
type InlineProps = Omit<AlertInlineProps, 'message'> & {alertVariant: EdsAlertVariant.Inline};
type AlertProps = (AlertFieldsFragment & BannerProps) | (AlertFieldsFragment & InlineProps);

const mapAlertMessages = (alertMessage: AlertProps['alertMessage']) => {
	// local assignment fixes eslint complaints
	const alertMessages = alertMessage;
	let alertJsx: JSX.Element[] | undefined;

	if (Array.isArray(alertMessages)) {
		alertJsx = alertMessages.map((msgProps, i) => (
			<Text {...msgProps} key={`alert-message-${i}`} />
		));
	}
	return alertJsx;
};

export const Alert = (props: AlertProps) => {
	const messages = useMessages();
	const [show, setShow] = useState(!props.hide);
	if (!show) return null;

	if (props.alertVariant === EdsAlertVariant.Inline) {
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const {hide, alertVariant, alertMessage, alertStatus, alertTitle, ...edsAlertProps} = props;

		const ovrProps: Partial<AlertInlineProps> = {};

		if (alertStatus === EdsStatus.Neutral) {
			ovrProps.className = css({
				border: 'none !important',
				background: 'none !important',
				'& div': {
					padding: '0 !important'
				},
				'& svg': {
					color: 'unset !important'
				}
			});
		} else {
			ovrProps.status = alertStatus as AlertInlineProps['status'];
		}

		return (
			<AlertInline
				{...edsAlertProps}
				{...ovrProps}
				message={
					<Stack gap={4}>
						{alertTitle && <Text {...alertTitle} />}
						{mapAlertMessages(alertMessage)}
					</Stack>
				}
			/>
		);
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const {hide, alertVariant, alertTitle, alertMessage, alertStatus, ...edsAlertProps} = props;

	if (alertVariant === EdsAlertVariant.Banner) {
		return (
			<AlertBanner
				{...edsAlertProps}
				isDismissable
				status={alertStatus!}
				message={mapAlertMessages(alertMessage)}
				closeLabel={messages['epic.app.close']}
				onClose={(e) => {
					setShow(false);
					props.onClose?.(e);
				}}
			/>
		);
	}

	return null;
};
